import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import billingUtils from 'app/shared/utils/billingUtils';

import ModalConstants from 'app/shared/constants/ModalConstants';
import LoadingConstants from 'app/shared/constants/LoadingConstants';

import ModalActions from 'app/shared/actions/ModalActions';
import BillingActions from 'app/shared/actions/BillingActions';

import { NOTIFICATION_TYPE } from 'app/client/workers/notificationModule';
import { Spinner } from '@zillow/zrs-comet';

import {
    Text,
    LabeledControl,
    FormField,
    Radio,
    Label,
    Checkbox,
    Button,
    TextButton,
    IconPlus,
    Spacer,
    Alert,
} from '@zillow/constellation';

import SpinnerContainer from 'app/shared/components/SpinnerContainer';
import BillingError from 'app/shared/components/Billing/BillingError';

import { StyledButtonGroup, StyledAlertText } from './StyledComponents';

const propTypes = {
    accountNumber: PropTypes.string.isRequired,
    submitLabel: PropTypes.string,
    creditCards: PropTypes.arrayOf(PropTypes.shape({
        paymentMethodId: PropTypes.string.isRequired,
        brand: PropTypes.string.isRequired,
        maskNumber: PropTypes.string.isRequired,
        expirationMonth: PropTypes.number.isRequired,
        expirationYear: PropTypes.number.isRequired,
    })).isRequired,
    onSubmit: PropTypes.func.isRequired,
    submitDisabled: PropTypes.bool,
};

const defaultProps = {
    submitLabel: 'Save',
    submitDisabled: false,
};

const BillingPaymentMethods = ({
    dispatch,
    accountNumber,
    submitLabel,
    accountPaymentMethodsLoadingState,
    creditCards,
    onSubmit,
    submitDisabled,
    paymentMethodLock,
    isPaymentInProgress,
    isSinglePayment,
    infoMessage,
    isAutopayEnabled,
    autopayMethodId,
    autoPayDisabled,
    allowRemove,
}) => {
    const [autoPay, setAutoPay] = React.useState(isAutopayEnabled);
    const [autoPayConditionsAccepted, setAutoPayConditionsAccepted] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [selectedCreditCardId, setSelectedCreditCardId] = React.useState(autopayMethodId);
    const [errorMessage, setErrorMessage] = React.useState(null);

    React.useEffect(() => {
        setSelectedCreditCardId(autopayMethodId);
        setAutoPay(isAutopayEnabled);
    }, [creditCards]);

    React.useEffect(() => {
        if (autoPayDisabled) {
            setAutoPay(false);
            setAutoPayConditionsAccepted(false);
        }
    }, [autoPayDisabled]);

    const handleGetPaymentMethods = () => {
        dispatch(BillingActions.getPaymentMethodsForAccount(accountNumber))
            .catch(() => {
                // todo display error
            });
    };

    const handleWebsocketMessge = (message) => {
        let data;
        try {
            data = JSON.parse(message.data);
        } catch (error) {
            return;
        }
        if (data.name !== NOTIFICATION_TYPE.BILLING_PORTAL_PAYMENT_METHODS_REFRESH) {
            return;
        }
        if (data.payload === accountNumber) {
            dispatch(BillingActions.setPaymentMethodLock(false));
            handleGetPaymentMethods(true);
        }
    };

    const handleCreditCardAdded = () => {
        dispatch(ModalActions.hideModal());
        dispatch(BillingActions.setPaymentMethodLock(true));
        dispatch(BillingActions.importPaymentMethods(accountNumber));
    };

    React.useEffect(() => {
        handleGetPaymentMethods(true, true);
        window.rentalManagerApiWebSocket.addEventListener('message', handleWebsocketMessge);
        return () => window.rentalManagerApiWebSocket.removeEventListener('message', handleWebsocketMessge);
    }, []);

    const canSubmit = () => {
        if (submitDisabled) return false;

        // If in edit card mode
        if (!isSinglePayment) {
            // no changes
            if ((isAutopayEnabled === autoPay && autopayMethodId)
                === selectedCreditCardId && selectedCreditCardId !== null) {
                return false;
            }
            // Autopay is on but user changed the card
            if (isAutopayEnabled === autoPay && autopayMethodId
                !== selectedCreditCardId && selectedCreditCardId !== null) {
                return autoPayConditionsAccepted;
            }
            if (isAutopayEnabled && !autoPay) {
                return true; // User turned off autopay
            }
            if (!isAutopayEnabled && autoPay && autopayMethodId === selectedCreditCardId) {
                // User toggles on autopay, and default is already selected
                return autoPayConditionsAccepted;
            }
            if (!isAutopayEnabled && autoPay) {
                return (autopayMethodId !== selectedCreditCardId)
                    && selectedCreditCardId !== null && autoPayConditionsAccepted;
            }
        }

        // this is in payment mode
        if (autoPay) return selectedCreditCardId !== null && autoPayConditionsAccepted;
        return selectedCreditCardId !== null;
    };

    const submit = () => {
        setIsSubmitting(true);
        dispatch(ModalActions.hideModal());
        dispatch(BillingActions.setPaymentMethodLock(true));
        onSubmit(selectedCreditCardId, autoPayConditionsAccepted)
            .then(() => {
                setErrorMessage(null);
                setIsSubmitting(false);
            }).catch((err) => {
                setErrorMessage(err.message);
                setIsSubmitting(false);
            });
    };

    const handleSubmit = () => {
        // only display modal if its the edit payment methods page AND user disabled autopay
        if (!isSinglePayment && isAutopayEnabled === true && !autoPay) {
            dispatch(ModalActions.showModal({
                modalType: ModalConstants.CONFIRMATION_MODAL,
                className: 'BillingCancelAutopay',
                header: 'Are you sure you want to cancel auto pay?',
                submitButtonTitle: 'Keep Auto Pay',
                cancelButtonTitle: 'Cancel Auto Pay',
                textContent: 'Auto pay lets you avoid the risk of missed payments. '
                    + 'If you cancel, you’ll have to pay manually each month.',
                useModalDialog: true,
                onCancel: submit,
                onSubmit: () => dispatch(ModalActions.hideModal()),
            }));
            return;
        }
        submit();
    };

    const handleRemoveCreditCardClick = (creditCard) => {
        const endingNumbers = creditCard.maskNumber.slice(creditCard.maskNumber.length - 4,
            creditCard.maskNumber.length);
        dispatch(ModalActions.showModal({
            modalType: ModalConstants.CONFIRMATION_MODAL,
            className: 'BillingRemoveCreditCard',
            header: `Are you sure you want to remove card ${endingNumbers}?`,
            submitButtonTitle: 'Yes, remove it',
            textContent: 'Disabling this credit card will not cancel any of your ongoing payment that use this card.' +
                'If you remove, you’ll have to add another credit card for the next bill.',
            useModalDialog: true,
            onCancel: () => dispatch(ModalActions.hideModal()),
            onSubmit: () => {
                setIsSubmitting(true);
                dispatch(BillingActions.setPaymentMethodLock(true));
                dispatch(ModalActions.hideModal());
                dispatch(BillingActions.deletePaymentMethod({
                    paymentMethodId: creditCard.paymentMethodId,
                    accountNumber,
                })).then((res) => {
                    if (!res.success) throw res.error.message;
                }).then(() => {
                    setErrorMessage(null);
                    setIsSubmitting(false);
                }).catch((err) => {
                    setErrorMessage(err.message);
                    setIsSubmitting(false);
                    dispatch(BillingActions.setPaymentMethodLock(false));
                });
            },
        }));
    };

    const handleAddCreditCardClick = () => {
        dispatch(ModalActions.showModal({
            className: 'BillingAddPaymentModal',
            header: 'Add Credit Card',
            onCreditCardAdded: handleCreditCardAdded,
            accountNumber,
            modalType: ModalConstants.BILLING_ADD_PAYMENT_MODAL,
            useModalDialog: true,
        }));
    };

    const showConfirmation = () => {
        if (isSinglePayment) {
            return autoPay && selectedCreditCardId;
        }
        if (autoPay) {
            return !isAutopayEnabled || (autopayMethodId !== selectedCreditCardId);
        }
        return false;
    };

    const isLoading = accountPaymentMethodsLoadingState === LoadingConstants.LOADING
        || accountPaymentMethodsLoadingState === LoadingConstants.INITIAL;
    const errorLoadingPaymentMethods = accountPaymentMethodsLoadingState === LoadingConstants.ERROR;

    const disableControls = () => {
        return isLoading || errorLoadingPaymentMethods || paymentMethodLock || isPaymentInProgress;
    };

    if (errorLoadingPaymentMethods) {
        return (
            <Alert
                appearance="error"
                body="Error loading payment methods, please try again later."
            />
        );
    }

    return (
        <React.Fragment>
            <FormField>
                <Label required={true}>
                    PAYMENT METHOD:
                </Label>
                {isLoading && <SpinnerContainer />}
                {paymentMethodLock && !isPaymentInProgress && (
                    <StyledAlertText>
                        <Text paddingRight="sm">
                            Please wait while we update your payment methods.
                        </Text>
                        <Spinner />
                    </StyledAlertText>
                )}
                {creditCards.map(creditCard => (
                    <LabeledControl
                        key={creditCard.paymentMethodId}
                        disabled={disableControls()}
                        controlId={`LabeledControl-credit-card-${creditCard.paymentMethodId}`}
                        label={(
                            <Label>
                                {`${creditCard.brand} ending with ${
                                    billingUtils.getCreditCardLastDigits(creditCard.maskNumber)}  `}
                                {allowRemove && !creditCard.defaultPaymentMethod
                                    && !paymentMethodLock && !isPaymentInProgress
                                    && (
                                        <TextButton
                                            onClick={() => handleRemoveCreditCardClick(creditCard)}
                                            disabled={disableControls()}
                                        >
                                            Remove
                                        </TextButton>
                                    )}
                                {autopayMethodId === creditCard.paymentMethodId && isAutopayEnabled && (
                                    <Text fontType="bodySmall">
                                        (Default)
                                    </Text>
                                )}
                                <br />
                                <Text fontType="bodySmall" fontColor="textLight">
                                    {`Expires ${creditCard.expirationMonth}/${creditCard.expirationYear}`}
                                </Text>
                            </Label>
                        )}
                        control={(
                            <Radio
                                name={`LabeledControl-credit-card-${creditCard.paymentMethodId}`}
                                checked={selectedCreditCardId === creditCard.paymentMethodId}
                                onChange={() => setSelectedCreditCardId(creditCard.paymentMethodId)}
                                disabled={disableControls()}
                            />
                        )}
                    />
                ))}
            </FormField>
            <Spacer marginY="sm">
                <Button
                    buttonType="secondary"
                    fluid={true}
                    icon={<IconPlus />}
                    onClick={handleAddCreditCardClick}
                    disabled={disableControls()}
                >
                    Add a credit card
                </Button>
            </Spacer>
            <FormField>
                <LabeledControl
                    controlId="LabeledControl-checkbox-autopay"
                    label={<Label>Auto Pay</Label>}
                    control={(
                        <Checkbox
                            name="LabeledControl-checkbox-autopay"
                            checked={autoPay}
                            onChange={() => {
                                setAutoPay(!autoPay);
                                setAutoPayConditionsAccepted(false);
                            }}
                        />
                    )}
                    disabled={autoPayDisabled || disableControls()}
                />
            </FormField>
            {showConfirmation() && (
                <React.Fragment>
                    <Spacer margin="sm">
                        <Spacer marginY="sm">
                            <Text fontType="smallbody" as="b">
                                Terms & Conditions for Auto Pay
                            </Text>
                        </Spacer>
                        <Text fontType="smallbody" as="p">
                            By clicking
                            {' '}
                            <Text fontType="smallbody" as="b">“Yes, I accept the Terms & Conditions”</Text>
                            {' '}
                            below,
                            you agree to enabling auto pay, which will automatically deduct from your selected payment
                            method on the 15th of each month.
                            You will be sent a summary of your account to be charged to your email address on record
                            by the 14th of each month. To cancel auto pay or to change your payment method,
                            visit the Payments Settings Page. Any changes for the current month must be made by the 14th
                            to avoid auto pay on the 15th.
                        </Text>
                    </Spacer>
                    <FormField>
                        <LabeledControl
                            controlId="LabeledControl-checkbox-autopay-conditions"
                            label={<Label>Yes, I accept the Terms & Conditions</Label>}
                            disabled={disableControls()}
                            control={(
                                <Checkbox
                                    name="LabeledControl-checkbox-autopay-conditions"
                                    checked={autoPayConditionsAccepted}
                                    onChange={() => setAutoPayConditionsAccepted(!autoPayConditionsAccepted)}
                                />
                            )}
                        />
                    </FormField>
                </React.Fragment>
            )}
            {infoMessage && (
                <Spacer margin="sm">
                    <Text fontType="smallbody" as="p">
                        *
                        {infoMessage}
                    </Text>
                </Spacer>
            )}
            {autoPayDisabled && (
                <Spacer margin="sm">
                    <Text fontType="smallbody" as="p">*Auto pay is only available if you pay the full balance.</Text>
                </Spacer>
            )}
            {errorMessage && (
                <BillingError errorMsg={errorMessage} />
            )}
            {isPaymentInProgress && (
                <StyledAlertText>
                    <Text paddingRight="sm">
                        Payment in progress.
                    </Text>
                    <Spinner />
                </StyledAlertText>
            )}
            <StyledButtonGroup>
                <Button
                    buttonType="primary"
                    disabled={!canSubmit() || isLoading || errorLoadingPaymentMethods
                        || isSubmitting || paymentMethodLock}
                    onClick={handleSubmit}
                >
                    {submitLabel}
                </Button>
            </StyledButtonGroup>
        </React.Fragment>
    );
};

const mapStateToProps = state => ({
    accountPaymentMethodsLoadingState: state.billing.accountPaymentMethods.loadingState,
    creditCards: state.billing.accountPaymentMethods.accountPaymentMethods,
    paymentMethodLock: state.billing.accountPaymentMethods.paymentMethodLock,
    isAutopayEnabled: state.billing.accountPaymentMethods.isAutopayEnabled,
    autopayMethodId: state.billing.accountPaymentMethods.autopayMethodId,
    autopayOwnerId: state.billing.accountPaymentMethods.autopayOwnerId,
    user: state.user,
    defaultPaymentMethod: state.billing.accountPaymentMethods.defaultPaymentMethod,
});

BillingPaymentMethods.propTypes = propTypes;
BillingPaymentMethods.defaultProps = defaultProps;

export { BillingPaymentMethods };
export default connect(mapStateToProps)(BillingPaymentMethods);
